import React from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'components/elements';
import {
  PageShell,
  PageHeader,
  PageTitle,
  EffectiveDate,
  Content,
  Intro,
  TableOfContents,
  Callout,
  ContactBlock,
} from './legal.styled';

const sections = [
  { id: 'about', title: 'About Polgozar' },
  { id: 'eligibility', title: 'Eligibility and Membership Tiers' },
  { id: 'the-service', title: 'The Service' },
  { id: 'google-calendar', title: 'Google Calendar Integration' },
  { id: 'community-guidelines', title: 'Community Guidelines' },
  { id: 'assumption-of-risk', title: 'Assumption of Risk' },
  { id: 'content-ip', title: 'Content and Intellectual Property' },
  { id: 'privacy', title: 'Privacy' },
  { id: 'termination', title: 'Termination' },
  { id: 'disclaimers', title: 'Disclaimers' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'disputes', title: 'Dispute Resolution and Governing Law' },
  { id: 'changes', title: 'Changes to These Terms' },
  { id: 'copyright', title: 'Copyright Concerns' },
  { id: 'miscellaneous', title: 'Miscellaneous' },
  { id: 'contact', title: 'Contact' },
];

export default function TermsOfService() {
  return (
    <PageShell>
      <Container>
        <PageHeader>
          <PageTitle>Terms of Service</PageTitle>
          <EffectiveDate>Effective Date: August 28, 2026</EffectiveDate>
        </PageHeader>

        <Content>
          <Intro>
            Welcome to Zoroastrian Polgozar. These Terms of Service (&quot;Terms&quot;) govern your access to and use of
            polgozar.org and related services (collectively, the &quot;Service&quot;), operated by The Zoroastrian Polgozar, a
            California nonprofit religious corporation. By creating an account or using the Service, you agree to these Terms. If you do not
            agree, please do not use the Service.
          </Intro>

          <TableOfContents aria-label="Table of contents">
            <h2>Contents</h2>
            <ol>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </TableOfContents>

          <h2 id="about">1. About Polgozar</h2>
          <p>
            Polgozar exists to serve the worldwide Zoroastrian community — which is geographically dispersed and
            numerically small — by connecting members, sharing community events, and offering religious and educational
            resources, including curriculum oriented toward youth and new members of the faith. Because many members of
            this community place a high value on privacy and safety, our Service is designed around minimal data
            collection and tiered, restricted access, as described further below and in our{' '}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>

          <h2 id="eligibility">2. Eligibility and Membership Tiers</h2>
          <h3>Full Membership</h3>
          <p>
            To become a Full Member, you must: (1) affirm in good faith that you identify as Zoroastrian, (2) register
            under your real, full name, and (3) provide the email addresses of two references — people who can
            personally confirm you are Zoroastrian. Approval currently requires an affirmative response from at least
            one of your two references; we may require both in the future as our verification process matures. We use
            this combination — attestation, real identity, and reference confirmation — because our community has told
            us that knowing who they&apos;re actually speaking with, and confirming new members through people who
            already know them, is essential to keeping the Service safe from impersonation and infiltration. We do not
            require religious documentation or third-party proof of faith.
          </p>

          <h3>Reference accountability</h3>
          <p>
            By providing a reference&apos;s contact information, you represent that you have a good-faith basis to
            believe that person can accurately confirm your Zoroastrian identity. By responding to a reference request,
            a reference represents that they are giving their honest, good-faith assessment. We retain the link between
            a member and their references for as long as that member&apos;s account remains active (see our{' '}
            <Link to="/privacy">Privacy Policy</Link>, Section 7), specifically so that if a member is later found to
            pose a safety risk, misrepresent who they are, or otherwise abuse the community&apos;s trust, we may follow
            up with the reference(s) who vouched for them to inform them of the issue and ask them to account for their
            attestation. A pattern of vouching for members who are later found to be fraudulent or unsafe may result in
            Polgozar disregarding that person&apos;s future references, and, if they are themselves a member, review or
            suspension of their own account.
          </p>
          <p>
            Full Members can view the community calendar, RSVP to and create events, message other Full Members (who are
            identified by their real name), and access community resources including the Ashavan Path curriculum.
          </p>

          <h3>Guests/Invitees</h3>
          <p>
            A Full Member may invite a non-Zoroastrian to a specific event. Guests may view and respond only to that
            single event. Guests cannot browse other events, cannot see the member directory or any Full Member&apos;s
            real name beyond what the inviting member chooses to share for that event, and cannot message any member of
            the Service, including the member who invited them. Guest access is intentionally limited to protect the
            privacy and safety of our broader membership.
          </p>

          <h3>Minors</h3>
          <p>
            Members under the age of 13 may join only through our parental consent process described in our{' '}
            <Link to="/privacy">Privacy Policy</Link>. A parent or legal guardian must create or approve the account
            before the child&apos;s information is collected, and may revoke consent or close the account at any time.
          </p>

          <h3>Accuracy</h3>
          <p>
            You agree to provide accurate account information, including your real name, and to keep your login
            credentials confidential. Your religious self-attestation remains a good-faith statement that we do not
            independently verify beyond the sponsorship requirement above.
          </p>

          <h2 id="the-service">3. The Service</h2>
          <p>Polgozar currently offers, and may add or modify over time:</p>
          <ul>
            <li>
              A community event calendar, including the ability for groups (such as ZYNA) to connect a Google Calendar
              for read-only event display (see Section 4);
            </li>
            <li>Messaging between Full Members;</li>
            <li>Educational and religious resources, including curriculum content; and</li>
            <li>Other community features we introduce from time to time.</li>
          </ul>
          <p>
            We may modify, suspend, or discontinue any part of the Service at any time, though we will make reasonable
            efforts to notify members of material changes affecting their ability to use core features.
          </p>

          <h2 id="google-calendar">4. Google Calendar Integration</h2>
          <p>
            If you connect a Google Calendar to Polgozar on behalf of your organization (e.g., as a ZYNA administrator),
            you represent and warrant that you have the necessary rights and authorization to share that calendar&apos;s
            event information through our Service. Our integration is read-only: Polgozar will display event information
            from the connected calendar but cannot create, modify, or delete events in your Google Calendar. You remain
            solely responsible for the accuracy of the events you choose to share, and for complying with Google&apos;s
            own Terms of Service in connecting your calendar. Polgozar is not responsible for the accuracy,
            completeness, or availability of event data pulled from a connected calendar.
          </p>

          <h2 id="community-guidelines">5. Community Guidelines</h2>
          <p>
            Because many of our members face real safety risks from being publicly identified as Zoroastrian, all users
            agree to:
          </p>
          <ul>
            <li>
              Never disclose, &quot;out,&quot; or attempt to identify another member&apos;s religious affiliation, real
              name, location, or other identifying information to anyone outside the Service, or to any Guest, without
              their explicit consent — the fact that Full Members see each other&apos;s real names is a trust tool for
              this community, not a license to share that information outside it;
            </li>
            <li>
              Never photograph, record, or share images or recordings of another member or event attendee without
              consent, particularly at community gatherings;
            </li>
            <li>
              Treat all members — Full Members and Guests alike — with respect, and refrain from harassment, hate
              speech, or discrimination;
            </li>
            <li>Not impersonate another person or misrepresent your affiliation with any organization; and</li>
            <li>Not use the Service for any unlawful purpose or to violate the rights of others.</li>
          </ul>
          <p>
            We reserve the right to remove content, suspend accounts, or take other action in response to violations of
            these Guidelines, at our reasonable discretion.
          </p>

          <h2 id="assumption-of-risk">6. Assumption of Risk — Exposure to Foreign Governments and Third Parties</h2>
          <Callout>
            <p>
              This section addresses a risk we take extremely seriously, and we want to state it plainly rather than
              bury it in boilerplate.
            </p>
          </Callout>

          <h3>The risk</h3>
          <p>
            Some members of our community — particularly Zoroastrians with ties to Iran, and especially converts — face
            a real risk of harm if they are identified as Zoroastrian by a hostile government or other bad actor. That
            harm can include, without limitation, harassment, prosecution, violence, or seizure of assets directed at
            the member or at their family and relatives. This risk is not created by Polgozar, and it cannot be fully
            eliminated by any website&apos;s design, community rules, or security measures — including ours. It can
            arise from things as ordinary as a photograph, a video, an audio recording, a name, or a statement made in
            connection with a religious ceremony or community event, whether that ceremony or event is organized through
            the Service, merely listed or promoted on the Service, or attended by someone who is also a member of the
            Service.
          </p>

          <h3>Voluntary assumption</h3>
          <p>
            By creating an account, attending or participating in any event listed, promoted, or organized through the
            Service, or appearing in, submitting, or being present when others create photographs, recordings, or
            statements connected to such an event, you acknowledge this risk and voluntarily assume it. This is true
            even where another member violates our Community Guidelines (Section 5) by taking or sharing a photograph or
            recording without consent — we prohibit that conduct and will act against members who violate it, but we
            cannot guarantee it will never happen, and once information leaves the Service (or leaves an in-person
            gathering) we cannot control or undo its further spread.
          </p>

          <h3>No liability for third-party or foreign government action</h3>
          <p>
            To the fullest extent permitted by law, Polgozar, its officers, volunteers, and affiliates are not
            responsible or liable for any damages, harm, loss, or adverse consequence — including but not limited to
            harassment, prosecution, violence, reputational harm, or loss of assets — arising from a member&apos;s or
            attendee&apos;s identification, exposure, or &quot;outing&quot; as Zoroastrian, whether that identification
            results from content posted on the Service, a photograph or recording taken at an event, a member&apos;s own
            statements or choices, or the actions of another member, a third party, or a foreign government. This
            assumption of risk does not excuse Polgozar from liability for our own gross negligence, willful misconduct,
            or violations of law, but it does mean we are not an insurer against the actions of third parties or foreign
            governments, or against the inherent risks of participating in a real-world religious community.
          </p>

          <h3>This applies to Guests too</h3>
          <p>
            Anyone attending an event through the Service — including Guests under Section 2 — assumes this same risk
            for that event, regardless of whether they hold a full account.
          </p>
          <p>
            We encourage every member and event organizer to keep taking sensible precautions — asking before
            photographing anyone, respecting a &quot;no photos&quot; request instantly and without argument, and
            thinking carefully about what identifying information is shared in any format — but this section makes clear
            that responsibility for that judgment call, and the underlying risk of participating in a visible religious
            community, rests with each individual, not with Polgozar.
          </p>

          <h2 id="content-ip">7. Content and Intellectual Property</h2>
          <p>
            <strong>Our content.</strong> The Ashavan Path curriculum, Polgozar branding, and other original materials
            we provide are owned by Polgozar (or licensed to us) and are provided for personal, educational, and
            community use. You may not reproduce or redistribute them commercially without our written permission.
          </p>
          <p>
            <strong>Your content.</strong> You retain ownership of content you submit to the Service (e.g., event
            descriptions, messages, posts). By submitting content, you grant Polgozar a non-exclusive, worldwide,
            royalty-free license to host, display, and distribute that content as necessary to operate the Service. You
            are solely responsible for content you submit and represent that you have the right to share it.
          </p>

          <h2 id="privacy">8. Privacy</h2>
          <p>
            Your use of the Service is also governed by our <Link to="/privacy">Privacy Policy</Link>, which describes
            what information we collect, how we use and protect it, and the special safeguards we&apos;ve built for
            at-risk members of our community. By using the Service, you consent to the practices described there.
          </p>

          <h2 id="termination">9. Termination</h2>
          <p>
            You may close your account at any time. We may suspend or terminate your access to the Service if you
            violate these Terms, the Community Guidelines, or applicable law, or if we reasonably believe your conduct
            poses a risk to another member&apos;s safety or to the Service. Where reasonably possible, we will provide
            notice and an opportunity to respond before termination, except in cases of urgent safety concerns.
          </p>

          <h2 id="disclaimers">10. Disclaimers</h2>
          <p>
            THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE,&quot; WITHOUT WARRANTIES OF ANY KIND,
            WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
            NON-INFRINGEMENT. WHILE WE TAKE MEMBER SAFETY AND PRIVACY SERIOUSLY AND DESIGN THE SERVICE ACCORDINGLY, WE
            CANNOT GUARANTEE ABSOLUTE ANONYMITY OR SECURITY, AND YOU USE THE SERVICE AT YOUR OWN RISK, INCLUDING THE
            SPECIFIC RISKS OF EXPOSURE DESCRIBED IN SECTION 6 ABOVE.
          </p>

          <h2 id="liability">11. Limitation of Liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, POLGOZAR AND ITS OFFICERS, VOLUNTEERS, AND AFFILIATES WILL NOT BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA,
            ARISING FROM YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR
            TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE WILL NOT EXCEED THE AMOUNT, IF ANY, YOU PAID US IN THE
            TWELVE MONTHS BEFORE THE CLAIM AROSE.
          </p>

          <h2 id="indemnification">12. Indemnification</h2>
          <p>
            You agree to indemnify and hold Polgozar harmless from any claims, damages, or expenses (including
            reasonable attorneys&apos; fees) arising from your violation of these Terms, your content, or your misuse of
            the Service.
          </p>

          <h2 id="disputes">13. Dispute Resolution and Governing Law</h2>
          <p>
            These Terms are governed by the laws of the State of California, without regard to conflict-of-laws
            principles. Before filing any formal claim, you agree to first contact us at{' '}
            <a href="mailto:admin@polgozar.org">admin@polgozar.org</a> so we can attempt to resolve the dispute
            informally. Any dispute that cannot be resolved informally will be subject to the exclusive jurisdiction of
            the state or federal courts located in California, and you consent to personal jurisdiction there.
          </p>

          <h2 id="changes">14. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will post the updated Terms on this page with a new
            effective date, and for material changes, we will provide additional notice. Continued use of the Service
            after changes take effect constitutes acceptance of the updated Terms.
          </p>

          <h2 id="copyright">15. Copyright Concerns</h2>
          <p>
            If you believe content on the Service infringes your copyright, please contact us at{' '}
            <a href="mailto:admin@polgozar.org">admin@polgozar.org</a> with a description of the work, its location on
            the Service, and your contact information, and we will investigate promptly.
          </p>

          <h2 id="miscellaneous">16. Miscellaneous</h2>
          <p>
            If any provision of these Terms is found unenforceable, the remaining provisions will remain in full effect.
            Our failure to enforce any provision is not a waiver of our right to do so later. These Terms, together with
            the <Link to="/privacy">Privacy Policy</Link>, constitute the entire agreement between you and Polgozar
            regarding the Service.
          </p>

          <h2 id="contact">17. Contact</h2>
          <ContactBlock>
            <strong>Polgozar</strong>
            Email: <a href="mailto:admin@polgozar.org">admin@polgozar.org</a>
            <br />
            6123 Oakbridge Dr., Granite Bay, CA 95746
          </ContactBlock>
        </Content>
      </Container>
    </PageShell>
  );
}
